import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  CreditCard, Building2, Save, AlertTriangle, CheckCircle2,
  Eye, EyeOff, Banknote, Settings, Shield
} from 'lucide-react';
import { PageHeader } from '../components/PageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PaymentSettings = () => {
  const { token, selectedCondominium } = useAuth();
  const condoId = selectedCondominium?.condominium?.id;
  const userRole = selectedCondominium?.role || 'RESIDENT';
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  
  const [config, setConfig] = useState({
    gateway_type: 'NONE',
    gateway_enabled: false,
    sumup_merchant_code: '',
    sumup_api_key: '',
    bank_name: '',
    bank_iban: '',
    bank_bic: '',
    bank_beneficiary: '',
    notify_admin_on_payment: true,
    auto_mark_paid_on_webhook: true
  });

  const fetchConfig = useCallback(async () => {
    if (!condoId || !token) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/payment-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setConfig(prev => ({
          ...prev,
          gateway_type: data.gateway_type || 'NONE',
          gateway_enabled: data.gateway_enabled || false,
          sumup_merchant_code: data.sumup_merchant_code || '',
          sumup_api_key: '', // Don't show existing key for security
          bank_name: data.bank_name || '',
          bank_iban: data.bank_iban || '',
          bank_bic: data.bank_bic || '',
          bank_beneficiary: data.bank_beneficiary || '',
          notify_admin_on_payment: data.notify_admin_on_payment ?? true,
          auto_mark_paid_on_webhook: data.auto_mark_paid_on_webhook ?? true,
          sumup_configured: data.sumup_configured || false,
          bank_configured: data.bank_configured || false
        }));
      }
    } catch (error) {
      toast.error('Errore caricamento configurazione');
    } finally {
      setLoading(false);
    }
  }, [condoId, token]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {
        gateway_type: config.gateway_type,
        gateway_enabled: config.gateway_enabled,
        sumup_merchant_code: config.sumup_merchant_code || null,
        bank_name: config.bank_name || null,
        bank_iban: config.bank_iban?.replace(/\s/g, '') || null,
        bank_bic: config.bank_bic || null,
        bank_beneficiary: config.bank_beneficiary || null,
        notify_admin_on_payment: config.notify_admin_on_payment,
        auto_mark_paid_on_webhook: config.auto_mark_paid_on_webhook
      };
      
      // Only include API key if changed
      if (config.sumup_api_key) {
        updateData.sumup_api_key = config.sumup_api_key;
      }
      
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/payment-config`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });
      
      if (res.ok) {
        toast.success('Configurazione salvata con successo');
        setConfig(prev => ({ ...prev, sumup_api_key: '' })); // Clear API key field
        fetchConfig(); // Refresh
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore salvataggio');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setSaving(false);
    }
  };

  // Only admins can access this page
  if (userRole === 'RESIDENT') {
    return (
      <div className="p-6">
        <Alert className="border-red-200 bg-red-50">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertTitle className="text-red-800">Accesso Negato</AlertTitle>
          <AlertDescription className="text-red-700">
            Questa sezione è riservata agli amministratori.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="payment-settings-page">
      <PageHeader
        title="Pagamenti"
        subtitle="Gateway e coordinate bancarie"
        actions={
          <Button onClick={handleSave} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Salvataggio...' : 'Salva Configurazione'}
          </Button>
        }
      />

      <Tabs defaultValue="gateway" className="space-y-4">
        <TabsList>
          <TabsTrigger value="gateway" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            Gateway Pagamenti
          </TabsTrigger>
          <TabsTrigger value="bank" className="flex items-center gap-2">
            <Banknote className="h-4 w-4" />
            Bonifico Bancario
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Impostazioni
          </TabsTrigger>
        </TabsList>

        {/* Gateway Tab */}
        <TabsContent value="gateway">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-600" />
                Gateway di Pagamento Online
              </CardTitle>
              <CardDescription>
                Permetti ai condomini di pagare le quote con carta di credito/debito
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Gateway Type */}
              <div className="space-y-2">
                <Label>Tipo Gateway</Label>
                <Select 
                  value={config.gateway_type} 
                  onValueChange={(value) => setConfig(prev => ({ ...prev, gateway_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona gateway" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NONE">Nessuno (solo bonifico)</SelectItem>
                    <SelectItem value="SUMUP">SumUp</SelectItem>
                    <SelectItem value="STRIPE" disabled>Stripe (prossimamente)</SelectItem>
                    <SelectItem value="NEXI" disabled>Nexi (prossimamente)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Enable/Disable */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <Label className="text-base">Abilita pagamenti online</Label>
                  <p className="text-sm text-muted-foreground">
                    I condomini potranno pagare con carta direttamente dalla piattaforma
                  </p>
                </div>
                <Switch
                  checked={config.gateway_enabled}
                  onCheckedChange={(checked) => setConfig(prev => ({ ...prev, gateway_enabled: checked }))}
                  disabled={config.gateway_type === 'NONE'}
                />
              </div>

              {/* SumUp Configuration */}
              {config.gateway_type === 'SUMUP' && (
                <div className="space-y-4 p-4 border rounded-lg bg-blue-50/50">
                  <h3 className="font-semibold flex items-center gap-2">
                    <img src="https://developer.sumup.com/images/logo.svg" alt="SumUp" className="h-5" />
                    Configurazione SumUp
                  </h3>
                  
                  {config.sumup_configured && (
                    <Alert className="border-green-200 bg-green-50">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <AlertTitle className="text-green-800">SumUp Configurato</AlertTitle>
                      <AlertDescription className="text-green-700">
                        Le credenziali SumUp sono già configurate. Inserisci nuovi valori solo se vuoi aggiornarle.
                      </AlertDescription>
                    </Alert>
                  )}
                  
                  <div className="grid gap-4">
                    <div className="space-y-2">
                      <Label>Merchant Code</Label>
                      <Input
                        value={config.sumup_merchant_code}
                        onChange={(e) => setConfig(prev => ({ ...prev, sumup_merchant_code: e.target.value }))}
                        placeholder="Es: MXXXXXXXX"
                      />
                      <p className="text-xs text-muted-foreground">
                        Trovi il Merchant Code nelle impostazioni del tuo account SumUp
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>API Key</Label>
                      <div className="flex gap-2">
                        <Input
                          type={showApiKey ? 'text' : 'password'}
                          value={config.sumup_api_key}
                          onChange={(e) => setConfig(prev => ({ ...prev, sumup_api_key: e.target.value }))}
                          placeholder={config.sumup_configured ? '••••••••••••••••' : 'sup_sk_...'}
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => setShowApiKey(!showApiKey)}
                        >
                          {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Genera la API Key dal portale developer.sumup.com
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bank Transfer Tab */}
        <TabsContent value="bank">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-emerald-600" />
                Dati Bancari per Bonifico
              </CardTitle>
              <CardDescription>
                Questi dati saranno mostrati ai condomini che scelgono di pagare via bonifico
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {config.bank_configured && (
                <Alert className="border-green-200 bg-green-50">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <AlertTitle className="text-green-800">Dati Bancari Configurati</AlertTitle>
                </Alert>
              )}
              
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Nome Banca</Label>
                  <Input
                    value={config.bank_name}
                    onChange={(e) => setConfig(prev => ({ ...prev, bank_name: e.target.value }))}
                    placeholder="Es: Unicredit, Intesa Sanpaolo"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>BIC/SWIFT (opzionale)</Label>
                  <Input
                    value={config.bank_bic}
                    onChange={(e) => setConfig(prev => ({ ...prev, bank_bic: e.target.value }))}
                    placeholder="Es: UNCRITM1XXX"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>IBAN</Label>
                <Input
                  value={config.bank_iban}
                  onChange={(e) => setConfig(prev => ({ ...prev, bank_iban: e.target.value }))}
                  placeholder="IT XX X XXXXX XXXXX XXXXXXXXXXXX"
                  className="font-mono"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Intestatario Conto</Label>
                <Input
                  value={config.bank_beneficiary}
                  onChange={(e) => setConfig(prev => ({ ...prev, bank_beneficiary: e.target.value }))}
                  placeholder="Es: Condominio Via Roma 1"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle>Impostazioni Notifiche</CardTitle>
              <CardDescription>
                Configura come gestire le notifiche dei pagamenti
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <Label className="text-base">Notifica pagamento ricevuto</Label>
                  <p className="text-sm text-muted-foreground">
                    Ricevi una notifica quando un condomino effettua un pagamento
                  </p>
                </div>
                <Switch
                  checked={config.notify_admin_on_payment}
                  onCheckedChange={(checked) => setConfig(prev => ({ ...prev, notify_admin_on_payment: checked }))}
                />
              </div>
              
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <Label className="text-base">Segna automaticamente come pagato</Label>
                  <p className="text-sm text-muted-foreground">
                    Quando SumUp conferma il pagamento, la quota viene segnata come saldata automaticamente
                  </p>
                </div>
                <Switch
                  checked={config.auto_mark_paid_on_webhook}
                  onCheckedChange={(checked) => setConfig(prev => ({ ...prev, auto_mark_paid_on_webhook: checked }))}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PaymentSettings;
